/**
 * The browsable catalog: the app's own tree of categories, and the models in each — the same
 * catalog the app shows, read through the catalog's API. The tree comes from the app's
 * `Catalog.xml`, cleaned and ordered the way the app does it for a floor-plan project
 * (tools/build-catalog-sections.ts); models come from `/models/search`.
 *
 * A list shows what the app's list shows (FurnitureItemsLoader, the catalog first since the app's
 * test https://trello.com/c/XL7RqmUG), in the catalog's order: every model people published and the
 * app's own ones alike, the app's own converted from their FBX archive, leaving out what the app
 * leaves out of a list (test accounts' models, plinths and ceiling moldings).
 */
import type { ModelRef } from '../core/hp/types';
import tree from './catalogTree.json';
import { fetchModels, modelIdOf, searchModels, thumbnailOf, type CatalogModel } from './catalogApi';
import { hasKey, language, t as tr, translate } from '../i18n/I18n';

/** A model document with the fields of it the drawer reads beyond the editor's own. */
export type CatalogDocument = CatalogModel & {
  /** The publishing account, as the search fills it in; a test account's models stay out of the catalog. */
  account?: { test?: boolean; role?: string };
  /** When it was published (ISO). */
  published?: string;
  /** The archive the web worker converts a model without a Unity bundle from (the app's built-ins). */
  source_reference?: string;
};

/** What a list asks the catalog for: any of `anyTags`, and all of `allTags`. */
export interface TagQuery { anyTags: string[]; allTags: string[] }

/** A chip that narrows a list: one of the app's hidden subcategories. */
export interface CatalogFilter extends TagQuery { id: string; title: string }

/** A folder (`children`) or a list of models (tags), as deep as the app's tree goes. */
export interface CatalogNode extends Partial<TagQuery> {
  /** The path of names from the root; names alone repeat across the tree. */
  id: string;
  name: string;
  title: string;
  photo?: string;
  /** The app's order for the list (`Catalog.xml` `sort`): `PriceHigh`, `PriceLow`, `Date`; popular without. */
  sort?: string;
  filters?: CatalogFilter[];
  children?: CatalogNode[];
}

export interface CatalogItem {
  /** The editor's model id: a cloud id, or a built-in's Unity path. */
  id: string;
  /** The catalog's own id of the model document. */
  catalogId: string;
  modelRef: ModelRef;
  title: string;
  section: string;
  /** Metres: width, height, depth. */
  size: [number, number, number];
  thumb?: string;
  tags: string[];
  /** True once the catalog holds a web bundle, or when the model needs none (`builtByCode`); false means the first placement queues one. */
  converted: boolean;
  /** Set for what the app builds by code rather than from a model (`builtByCode`): a bare opening, a wall of its own. */
  builtByCode?: CodeBuilt;
  placement: 'floor' | 'wall' | 'ceiling';
  /** The model's definition: its name and marks on the card (catalogCard.ts). */
  definition?: string;
  /** When the catalog published it (ISO): NEW until the definition says `Added`. */
  published?: string;
  is2d?: boolean;
}

export const PAGE_SIZE = 40;

/**
 * The tree with its titles in the interface language. catalogTree.json carries the Russian ones; the others come from
 * the app's Langs through src/i18n/langs/catalog (tools/build-catalog-langs.ts), keyed by the node's id.
 */
export function catalogTree(): CatalogNode[] {
  const code = language();
  let nodes = titledTrees.get(code);
  if (!nodes) titledTrees.set(code, nodes = (tree as CatalogNode[]).map(titled));
  return nodes;
}
const titledTrees = new Map<string, CatalogNode[]>();
const titleOf = (key: string, title: string) => hasKey(key) ? translate(key) : title;
function titled(node: CatalogNode): CatalogNode {
  const key = '@catalog/' + node.id;
  return { ...node, title: titleOf(key, node.title),
    ...(node.filters ? { filters: node.filters.map(filter => ({ ...filter, title: titleOf(`${key}#${filter.id}`, filter.title) })) } : {}),
    ...(node.children ? { children: node.children.map(titled) } : {}) };
}

/** The first list with this name anywhere in the tree (`Doors`, `Windows`). */
export function findCatalogList(name: string, nodes: CatalogNode[] = catalogTree()): CatalogNode | undefined {
  for (const node of nodes) {
    if (!node.children && node.name === name) return node;
    const deeper = node.children && findCatalogList(name, node.children);
    if (deeper) return deeper;
  }
  return undefined;
}

/** A list narrowed by one of its chips: the chip's tags on top of the list's own. */
export function narrowed(list: CatalogNode, filter?: CatalogFilter): TagQuery {
  const own = { anyTags: list.anyTags ?? [], allTags: list.allTags ?? [] };
  if (!filter) return own;
  return { anyTags: filter.anyTags.length ? filter.anyTags : own.anyTags, allTags: [...new Set([...own.allTags, ...filter.allTags])] };
}

/** Config.xml «WallFurniture», «FloorFurniture» and «CeilingFurniture» (ConfigStorage.*FurnitureTags), verbatim. */
const WALL_FURNITURE = new Set('Wall,WiringDevices,KitchenCabinetFacade,PantryWall,Painting,Sticker,Stucco,Phytowall,WallDecor,Appliances,Aquarium,Curtains,Mirror,SquareMirror,RectangleMirror,RoundMirror,OvalMirror,AbstractMirror,BacklightMirror,KidsMirror,ArchedMirror,Window,Door,Entrance,Arch,GarageDoor,Gap,Niche,WallSconces,WallShelve,Drawer,KidsDrawer,BuiltinWardrobe,Wardrobe,TVset,TVStorage,TVStand,TVWall,FloorCab,WallCabinetRegular,ShoeCabinet,Dresser,Sideboard,ConsoleTable,Bookcase,Bookshelve,Plinth,CeilingMolding,Moldings,Toilet,WallToilet,CanopyHood,TshapedHood,TelescopicHood,BuiltinHood,AngledHood,WallCabinet,KitchenShelf,KitchenWall,HighCabinet,Dishwasher,BuiltinDishwasher,BaseCabinet,BathroomWall,Urinal,ShowerWall,Ovens,Range,TowelStand,Radiator,WallClock,WallPlant,Signboards,FloorCab,HighCab,WallPanel,Poster,TowelRack,HandDryer,WallDoor,AbstractPainting,CurtainsWall,MemoBoard,StorageUnit,GamingWallDecor,CeilingWallLamp,WallTap,SinkWall,KidsArt,Painting3D,NoFrame,ArtSet,ClassicArt,PhotoArt,TextArt,KidsArt,RoundArt,SeaArt,AsianArt,AbstractArt,ModernArt,OilPainting,WoodArt,Maps,Reproduction,Shutter,Stucco3D,WallPipe,GymWall,Flush,ArmedWallLamp,FlushMountWallLamp,CandleWallLamp,SwingArmWallLamp,Wallchiere,SpotlightWallLamp,RecessedWallLamp,BarnWallLamp,HalfMoonWallLamp,PictureWallLamp,UnderCabinetLight,VanityWallLamp,TrackWallLamp,NeonWallLamp,CornerWallLamp,DoorNightstand,DrawerNightstand,1DrawerNightstand,DoorNightstand,ShelveNightstand,RoundNightstand,CornerNightstand,NoLegsNightstand,SolidNightstand,WallNightstand,HangingHammock,Pictures,SinkCabinet,KitchenSink,FloorSink,TowelDryer'.split(','));
const FLOOR_FURNITURE = new Set('Roofs,InteriorWall,PantryWall,Floor,PlayTents,Bath,Refrigerators,Piano,Guitar,Shower,FloorVase,Plant,Pouf,Basket,Door,Entrance,Arch,GarageDoor,FloorMirror,Fireplace,Wardrobe,TVset,TVStorage,TVStand,TV,FloorCab,ShoeCabinet,Dresser,Drawer,KidsDrawer,People,CharacterOther,Cat,Dog,Sideboard,ConsoleTable,Bookcase,Bookshelve,BuiltinWardrobe,Toilet,ToiletKid,DressingTable,KitchenCart,Bench,HighCabinet,BaseCabinet,Dishwasher,BuiltinDishwasher,KidChair,KidTable,Table,Toys,Buffet,SideTable,Sofa,CornerSofa,Stairs,Chaise,Lounge,Bed,JuniorBed,Crib,StandardCrib,ConvertibleCrib,RattanCrib,MeshCrib,Cradle,StorageCrib,OvalCrib,CribDecor,Footstool,Chair,JuniorChair,Armchair,BarStool,Stool,OfficeChair,RockingChair,Ottoman,ArmchairWithOttoman,Paravent,FloorLamp,ReadingFloorLamp,CoffeeTable,BarTable,TrashCan,Cart,CoatStand,Desk,DiningSet,Vase,Ovens,Range,Gym,TowelStand,AnimalFurniture,Car,Bike,Billiard,GameTable,StairsOpening,StairsOpeningRound,FloorCab,HighCab,StorageUnit,GamingChair,GamingSetup,GamingTable,PaintingFloor,WallDoor,StorageBench,WoodenBench,UpholsteredBench,OvalBench,ArmrestBench,BackBench,ShelveBench,DoorNightstand,DrawerNightstand,1DrawerNightstand,ShelveNightstand,RoundNightstand,CornerNightstand,NoLegsNightstand,SolidNightstand,SwingChair,StandingHammock,StandSwings,FloorSink,SinkCabinet,KitchenSink'.split(','));
const CEILING_FURNITURE = new Set('Ceiling,CeilingToys,CeilingLamp,CookerHoodIsland,ShowerCeiling,CurtainsCeiling,CeilingBeams,Skylight,CeilingLED,ChandelierLamp,PendantLamp,TrackLight,SpotLights,KitchenIslandLights,SemiFlushMountLights,FlushMountLights,TelescopicLamp,CeilingWallLamp,CeilingDecor,CeilingConstructions,Stucco3D,Stucco,CeilingFan,HangingSwings'.split(','));

/** The app's `FurnitureInfo.IsWallFurniture`. */
export function isWallFurniture(tags: readonly string[]): boolean {
  return tags.some((x) => x === 'Wall' || WALL_FURNITURE.has(x));
}

/**
 * A model's own place by its tags in the app's order (`ProjectUIHelper.IsCeilingPlacement`, `CompanionPlacer.ChooseMount`):
 * the ceiling, a wall piece that is no floor piece, else the floor, where a card with a wall tag stands against a wall.
 */
export function placementOf(tags: string[]): CatalogItem['placement'] {
  const wall = isWallFurniture(tags);
  if (tags.some((x) => x === 'CeilingLamp' || x === 'SloppedCeiling') || (!wall && tags.some((x) => CEILING_FURNITURE.has(x)))) return 'ceiling';
  return wall && !floorTagged(tags) ? 'wall' : 'floor';
}

const floorTagged = (tags: readonly string[]) => tags.some((x) => x === 'Floor' || FLOOR_FURNITURE.has(x));

/** The app's `FurnitureInfo.GetFurnitureType` without its opening and wall bits: a floor piece, else a wall one, else a ceiling one. */
export function furnitureType(tags: readonly string[]): CatalogItem['placement'] {
  if (floorTagged(tags)) return 'floor';
  if (isWallFurniture(tags)) return 'wall';
  return tags.some((x) => CEILING_FURNITURE.has(x)) ? 'ceiling' : 'floor';
}

/** A model document as a drawer card. */
export function catalogItemOf(model: CatalogDocument, section = ''): CatalogItem {
  const id = modelIdOf(model);
  const modelRef: ModelRef = model.builtinId
    ? { kind: 'local', id, raw: 'Models/' + id }
    : { kind: 'cloud', id, raw: id };
  const cm = (value: number | undefined, fallback: number) => (Number.isFinite(value) && (value as number) > 0 ? (value as number) / 100 : fallback);
  const tags = model.tags ?? [];
  const code = builtByCode(model);
  return {
    id, catalogId: model.id, modelRef,
    title: model.title?.trim() || (model.is2d ? tr('#CatalogStatePicture') : tr('#CatalogStateModel')),
    section,
    size: [cm(model.width, .5), cm(model.height, .5), cm(model.depth, .5)],
    thumb: thumbnailOf(model),
    tags,
    converted: !!model.bundles?.web || !!code,
    ...(code ? { builtByCode: code } : {}),
    placement: placementOf(tags),
    ...(typeof model.definition === 'string' && /^https?:\/\//.test(model.definition) ? { definition: model.definition } : {}),
    ...(model.published ? { published: model.published } : {}),
    ...(model.is2d ? { is2d: true } : {}),
  };
}

/** Config.xml «OpeningFurniture» (ConfigStorage.OpeningFurnitureTags) and «InteriorWallFurniture» (ConfigStorage.WallTags). */
const OPENING_TAGS = new Set(['Door', 'Entrance', 'Window', 'Arch', 'GarageDoor', 'Gap', 'Niche', 'WallDoor']);
const WALL_TAGS = new Set(['InteriorWall']);

export type CodeBuilt = 'opening' | 'wall';

/**
 * What the app builds by code instead of loading a model (FurnitureVariation.CreateFurniture): an opening with no model
 * of its own is a bare hole in the wall, an interior wall without one is a wall. The app's own model with neither a
 * bundle, nor a source archive, nor a 2D definition has no model: the conversion queue can only fail it, so it is never
 * asked. Its tags say which of the two it is, the opening first as the app checks it.
 */
export function builtByCode(model: CatalogDocument): CodeBuilt | null {
  if (!model.builtinId || model.is2d || model.source_reference || model.bundle || Object.values(model.bundles ?? {}).some(Boolean)) return null;
  const tags = model.tags ?? [];
  if (tags.some((tag) => OPENING_TAGS.has(tag))) return 'opening';
  if (tags.some((tag) => WALL_TAGS.has(tag))) return 'wall';
  return null;
}

/**
 * What the drawer shows: everything the search answers with that the worker can convert — a
 * Unity bundle, a 2D definition (a picture, a sticker, named or not), or the FBX archive with
 * the definition beside it (the app's own models), or a web bundle already made — and what
 * the app builds by code and needs no model (`builtByCode`).
 */
export function showable(model: CatalogDocument): boolean {
  const bundles = model.bundles ?? {};
  if (Object.values(bundles).some(Boolean) || !!model.bundle || !!builtByCode(model)) return true;
  return !!model.definition && (!!model.is2d || !!model.source_reference);
}

/**
 * What the app leaves out of a list (SearchFurnitureVariations.SearchVariationsPaged): plinths and
 * ceiling moldings, which run along the walls rather than being placed, and the models of a test
 * account (AssetMetaInfo.IsTest).
 */
export function excluded(model: CatalogDocument): boolean {
  const tags = model.tags ?? [];
  if (tags.includes('Plinth') || tags.includes('CeilingMolding')) return true;
  return !!model.account?.test && model.account.role !== 'admin';
}

/** Where a listing goes on: the pass of a search (a list has one) and the catalog's skip in it. */
export interface PageCursor { pass: number; skip: number }

/** A page as the drawer needs it: the cards, where the next page starts, and how many models the catalog gave. */
export interface CatalogPage {
  items: CatalogItem[];
  next: PageCursor;
  more: boolean;
  /** What the catalog answered, shown or not: the app's estimate of a list's size counts it. */
  raw: number;
}

const cursorOf = (at: PageCursor | number | undefined): PageCursor => typeof at === 'number' ? { pass: 0, skip: at } : at ?? { pass: 0, skip: 0 };

function pageOf(models: CatalogDocument[], cursor: PageCursor, section: string, passes = 1): CatalogPage {
  const items = models.filter((model) => showable(model) && !excluded(model)).map((model) => catalogItemOf(model, section));
  // `next` counts what the catalog answered, not what is shown: some of it is not showable.
  const full = models.length >= PAGE_SIZE;
  const next: PageCursor = full ? { pass: cursor.pass, skip: cursor.skip + models.length } : { pass: cursor.pass + 1, skip: 0 };
  return { items, next, more: full || next.pass < passes, raw: models.length };
}

/** One page of a list's models, optionally narrowed by one of its chips. */
export async function loadList(list: CatalogNode, filter?: CatalogFilter, at?: PageCursor | number): Promise<CatalogPage> {
  const cursor = cursorOf(at);
  const models = await searchModels({ ...narrowed(list, filter), skip: cursor.skip, pagesize: PAGE_SIZE });
  return pageOf(models, cursor, list.id);
}

/** One page of models for a search: each of the searches in turn, the next once the catalog has no more for this one. */
export async function searchCatalog(terms: string[], at?: PageCursor | number): Promise<CatalogPage> {
  const cursor = cursorOf(at);
  const term = terms[cursor.pass]?.trim();
  if (!term) return { items: [], next: cursor, more: false, raw: 0 };
  const models = await searchModels({ term, skip: cursor.skip, pagesize: PAGE_SIZE });
  return pageOf(models, cursor, '', terms.length);
}

/** The card for a model already in a project, or undefined when the catalog has no such model. */
export async function findCatalogItem(modelId: string): Promise<CatalogItem | undefined> {
  const found = await fetchModels([modelId]);
  const model = found[modelId];
  return model ? catalogItemOf(model) : undefined;
}
